import { useMemo } from "react";
import { Box, Chip, Divider, Grid, Paper, Stack, Typography } from "@mui/material";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

import OtrosConceptosVisita from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/OtrosConceptosVisita";
import type { ConceptoVisita } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/OtrosConceptosVisita";
import PuntosDeRedVisita from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/PuntosDeRedVisita";
import type { PuntoRed } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/PuntosDeRedVisita";
import { usePreciosConceptosVisita } from "@/erp/orden-servicio/orden-servicio-visita/components/otros-conceptos/usePreciosConceptosVisita";
import type { FotoEvidencia } from "@/erp/orden-servicio/orden-servicio-visita/components/evidencias/CampoFotoEvidencia";
import type { AprobacionConceptosTraslado } from "./conceptos-traslado.utils";
import { totalConceptos } from "./conceptos-traslado.utils";

const ACCENT = "#1565c0";

export type ConceptosTrasladoSectionProps = {
  conceptos?: ConceptoVisita[] | null;
  onChangeConceptos?: (v: ConceptoVisita[]) => void;
  puntosRed?: PuntoRed[] | null;
  onChangePuntosRed?: (v: PuntoRed[]) => void;
  readOnly?: boolean;
  /** Ubicación de la flota: habilita elegir serie por concepto y validar stock. */
  ubicacionFlota?: string;
  stockPorProducto?: Record<string, number>;
  /** Modo técnico: solo elige serie/fotos (los conceptos los definió otro rol). */
  serieEditableOnly?: boolean;
  onUploadFoto?: (file: File) => Promise<FotoEvidencia>;
  /** Para el aviso de "falta CABLE UTP en materiales" (solo técnico). */
  tieneCableUtp?: boolean;
  /** Estado de aprobación (chip informativo). */
  aprobacion?: AprobacionConceptosTraslado | null;
  title?: string;
  subtitle?: string;
  /** Contenido extra bajo la sección (botones de guardar/aprobar). */
  footer?: React.ReactNode;
  /** Oculta el bloque de puntos de red. */
  hidePuntosRed?: boolean;
  /**
   * Bloque que va debajo de "Puntos de red (cable UTP)": el "Excedente de
   * fibra" (ExcedenteFibraTraslado). Si es null/false no se pinta nada.
   */
  despuesDePuntosRed?: React.ReactNode;
};

/**
 * Sección "Otros conceptos del traslado" + "Puntos de red": reutiliza los
 * componentes de visitas pero con el catálogo, métodos de pago y config de
 * cable UTP ligados a TRASLADOS (independientes de los de visitas).
 */
export default function ConceptosTrasladoSection({
  conceptos,
  onChangeConceptos,
  puntosRed,
  onChangePuntosRed,
  readOnly = false,
  ubicacionFlota,
  stockPorProducto,
  serieEditableOnly = false,
  onUploadFoto,
  tieneCableUtp = true,
  aprobacion,
  title = "Otros conceptos del traslado",
  subtitle,
  footer,
  hidePuntosRed = false,
  despuesDePuntosRed,
}: ConceptosTrasladoSectionProps) {
  const {
    conceptos: catalogo,
    metodosPago,
    puntoRed,
    ivaPorcentaje,
  } = usePreciosConceptosVisita("TRASLADO");

  const items = Array.isArray(conceptos) ? conceptos : [];
  const puntos = Array.isArray(puntosRed) ? puntosRed : [];

  // Conceptos del traslado a los que se les puede ligar un punto de red.
  const conceptosConRed = useMemo(
    () =>
      items
        .filter((c) => catalogo.find((cat) => cat.concepto === c.concepto)?.permite_punto_red)
        .map((c) => c.concepto)
        .filter((v, i, a) => !!v && a.indexOf(v) === i),
    [items, catalogo]
  );

  const total = totalConceptos(items, puntos);
  const estadoAprob = aprobacion?.estado;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 2.5 }, borderRadius: 2 }}>
      <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Box sx={{ color: ACCENT, display: "flex", mt: 0.25 }}>
          <Inventory2OutlinedIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="subtitle1" fontWeight={800}>
              {title}
            </Typography>
            {estadoAprob === "PENDIENTE" && (
              <Chip size="small" color="warning" variant="outlined" label="Pendiente de aprobación (Activaciones)" />
            )}
            {estadoAprob === "APROBADO" && (
              <Chip
                size="small"
                color="success"
                label={`Aprobado por Activaciones${aprobacion?.aprobado_por ? ` · ${aprobacion.aprobado_por}` : ""}`}
              />
            )}
            {total > 0 && (
              <Chip size="small" label={`Total: $${total.toFixed(2)}`} sx={{ fontWeight: 700 }} />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {subtitle ||
              "Equipos/servicios adicionales cobrados en el traslado, con su método de pago. Catálogo y métodos propios de traslados (Catálogos › Administración › Traslados)."}
          </Typography>
        </Box>
      </Stack>

      <OtrosConceptosVisita
        value={items}
        onChange={onChangeConceptos}
        readOnly={readOnly}
        conceptos={catalogo}
        metodosPago={metodosPago}
        ubicacionFlota={ubicacionFlota}
        stockPorProducto={stockPorProducto}
        serieEditableOnly={serieEditableOnly}
        onUploadFoto={onUploadFoto}
        title="Conceptos"
      />

      {!hidePuntosRed && (
        <>
          <Divider sx={{ my: 2 }} />
          <PuntosDeRedVisita
            value={puntos}
            onChange={onChangePuntosRed}
            readOnly={readOnly}
            config={puntoRed}
            ivaPorcentaje={ivaPorcentaje}
            tieneCableUtp={tieneCableUtp}
            conceptosDisponibles={conceptosConRed}
            onUploadFoto={onUploadFoto}
            title="Puntos de red (cable UTP)"
          />
        </>
      )}

      {despuesDePuntosRed && (
        <>
          <Divider sx={{ my: 2 }} />
          {despuesDePuntosRed}
        </>
      )}

      {footer && (
        <Grid container item justifyContent="flex-end" alignItems="center" gap={1} sx={{ mt: 2 }}>
          {footer}
        </Grid>
      )}
    </Paper>
  );
}
